import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { KeyRound, ArrowRight } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { api, BASE_URL } from '../lib/api'

type View = 'main' | 'sso'

export function LoginPage() {
  const [searchParams] = useSearchParams()
  const sessionExpired = searchParams.get('reason') === 'session_expired'

  const [view, setView] = useState<View>('main')
  const [domain, setDomain] = useState('')
  const [domainError, setDomainError] = useState('')
  const { user } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true })
  }, [user, navigate])

  function handleSsoSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = domain.trim().toLowerCase()
    if (!trimmed || !trimmed.includes('.')) {
      setDomainError('Enter a valid email domain, e.g. acme.com')
      return
    }
    // Redirect to SSO login — worker will look up IdP config by domain
    window.location.href = api.getSsoLoginUrl(trimmed)
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{background: '#F5F2EA'}}>
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 text-3xl">
            🕊
          </div>
          <h1 className="text-2xl font-bold tracking-tight" style={{color: '#1a3a40', fontFamily: 'Georgia, serif'}}>Duva</h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>Small nudges. Real change.</p>
        </div>

        {sessionExpired && (
          <div
            className="mb-4 rounded-xl border px-4 py-3 text-sm"
            role="status"
            style={{ background: '#EBF6FB', borderColor: '#a7d8e0', color: '#1a3a40' }}
          >
            Your session expired. Please sign in again.
          </div>
        )}

        {view === 'main' ? (
          <MainView onSsoClick={() => setView('sso')} />
        ) : (
          <SsoView
            domain={domain}
            setDomain={setDomain}
            domainError={domainError}
            setDomainError={setDomainError}
            onSubmit={handleSsoSubmit}
            onBack={() => { setView('main'); setDomain(''); setDomainError('') }}
          />
        )}

        <p className="text-center text-xs mt-6" style={{color: '#9aafb3'}}>
          Free for teams up to 50 people · No credit card required
        </p>
      </div>
    </div>
  )
}

// ── Main sign-in view ─────────────────────────────────────────────────────────

function MainView({ onSsoClick }: { onSsoClick: () => void }) {
  return (
    <div className="rounded-2xl p-8" style={{background: '#FDFCF8', border: '1px solid #e0d9cc', boxShadow: '0 2px 16px rgba(42,122,138,0.07)'}}>
      <h2 className="text-lg font-semibold mb-1" style={{color: '#1a3a40'}}>Sign in to your workspace</h2>
      <p className="text-sm mb-6" style={{color: '#6b8a8f'}}>
        Connect your Slack workspace to get started. No password required.
      </p>

      {/* Slack OAuth button */}
      <a
        href={api.getSlackInstallUrl()}
        className="flex items-center justify-center gap-3 w-full text-white font-semibold py-3 px-4 rounded-xl transition-colors"
        style={{background: '#4A154B'}}
        onMouseEnter={e => (e.currentTarget.style.background = '#611a5d')}
        onMouseLeave={e => (e.currentTarget.style.background = '#4A154B')}
      >
        <SlackIcon />
        Sign in with Slack
      </a>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px" style={{background: '#e0d9cc'}} />
        <span className="text-xs" style={{color: '#9aafb3'}}>or</span>
        <div className="flex-1 h-px" style={{background: '#e0d9cc'}} />
      </div>

      {/* Teams button */}
      <a
        href={`${BASE_URL}/api/auth/teams/install`}
        className="flex items-center justify-center gap-3 w-full text-white font-semibold py-3 px-4 rounded-xl transition-colors"
        style={{background: '#5059C9'}}
      >
        <TeamsIcon />
        Sign in with Microsoft Teams
      </a>
      <p className="text-[11px] mt-2 mb-3 leading-snug" style={{ color: '#9aafb3' }}>
        Identity is managed by your organization. No passwords are stored by Duva.
      </p>

      {/* SSO button */}
      <button
        onClick={onSsoClick}
        className="flex items-center justify-center gap-3 w-full font-semibold py-3 px-4 rounded-xl transition-colors"
        style={{background: '#f0ece4', border: '1px solid #d5cdc0', color: '#3d6068'}}
      >
        <KeyRound size={18} style={{color: '#6b8a8f'}} />
        Sign in with SSO
        <ArrowRight size={15} className="ml-auto" style={{color: '#9aafb3'}} />
      </button>
    </div>
  )
}

// ── SSO domain entry view ─────────────────────────────────────────────────────

function SsoView({ domain, setDomain, domainError, setDomainError, onSubmit, onBack }: {
  domain: string
  setDomain: (v: string) => void
  domainError: string
  setDomainError: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
  onBack: () => void
}) {
  return (
    <div className="rounded-2xl p-8" style={{background: '#FDFCF8', border: '1px solid #e0d9cc', boxShadow: '0 2px 16px rgba(42,122,138,0.07)'}}>
      <button
        onClick={onBack}
        className="text-xs mb-5 flex items-center gap-1 transition-colors"
        style={{color: '#9aafb3'}}
      >
        ← Back
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{background: '#EBF6FB', border: '1px solid #a7d8e0'}}>
          <KeyRound size={16} style={{color: '#2A7A8A'}} />
        </div>
        <div>
          <h2 className="text-base font-semibold" style={{color: '#1a3a40'}}>Sign in with SSO</h2>
          <p className="text-xs mt-0.5" style={{color: '#9aafb3'}}>Enter your company email domain</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-xs mb-1.5" style={{color: '#6b8a8f'}}>Work email domain</label>
          <input
            type="text"
            autoFocus
            placeholder="acme.com"
            value={domain}
            onChange={e => { setDomain(e.target.value); setDomainError('') }}
            className="w-full rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none transition-colors"
            style={{background: '#f0ece4', border: '1px solid #d5cdc0', color: '#1a3a40'}}
          />
          {domainError && (
            <p className="text-xs mt-1.5" style={{color: '#e05a5a'}}>{domainError}</p>
          )}
          <p className="text-xs mt-1.5" style={{color: '#9aafb3'}}>
            We'll look up your organization's identity provider and redirect you there.
          </p>
        </div>

        <button
          type="submit"
          disabled={!domain.trim()}
          className="flex items-center justify-center gap-2 w-full text-white font-semibold py-3 px-4 rounded-xl transition-colors disabled:opacity-50"
          style={{background: '#2A7A8A'}}
        >
          Continue with SSO
          <ArrowRight size={16} />
        </button>
      </form>

      <p className="text-xs mt-5 text-center" style={{color: '#9aafb3'}}>
        SSO must be configured by your admin in Settings → Single Sign-On before use.
      </p>
    </div>
  )
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function SlackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 122.8 122.8" fill="none">
      <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9z" fill="#E01E5A"/>
      <path d="M32.3 77.6c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="#E01E5A"/>
      <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2z" fill="#36C5F0"/>
      <path d="M45.2 32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="#36C5F0"/>
      <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2z" fill="#2EB67D"/>
      <path d="M90.5 45.2c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="#2EB67D"/>
      <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9z" fill="#ECB22E"/>
      <path d="M77.6 90.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="#ECB22E"/>
    </svg>
  )
}

function TeamsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 2228.833 2073.333" fill="none">
      <path d="M1554.637 777.5h575.713c54.391 0 98.483 44.092 98.483 98.483v524.398c0 199.901-162.084 361.985-361.984 361.985h-1.229c-199.9.001-361.984-162.083-361.984-361.984V828.971c.001-28.427 23.044-51.471 51.001-51.471z" fill="#5059C9"/>
      <path d="M1943.25 672.917c120.793 0 218.75-97.957 218.75-218.75s-97.957-218.75-218.75-218.75-218.75 97.957-218.75 218.75 97.957 218.75 218.75 218.75z" fill="#5059C9"/>
      <path d="M1218.25 672.917c163.227 0 295.583-132.357 295.583-295.584C1513.833 214.107 1381.477 81.75 1218.25 81.75S922.667 214.107 922.667 377.333c0 163.227 132.356 295.584 295.583 295.584z" fill="#7B83EB"/>
      <path d="M1651.349 777.5H785.151A98.468 98.468 0 00687 876.069V1572.5c0 .5.12 1 .12 1.5 0 398.395 323.095 721.5 721.5 721.5s721.5-323.105 721.5-721.5v-696.81A98.219 98.219 0 001651.349 777.5z" fill="#7B83EB"/>
    </svg>
  )
}
